/** 经营总览：应收与超期 */

import { buildCompositionChartRows } from '../../../charts/composition/lib/compositionChartBuckets';
import { COMPOSITION_CHART_DIMENSION, COMPOSITION_CHART_METRIC } from '../../../charts/composition/lib/compositionChartConfig';
import { CHART_BUCKETS_RECENT_START_INDEX, CHART_LABELS } from '../../../../dashboard/overdue-statistic/overdueChartConstants';

/** 详情完整排行条数；小卡另截前 5。须与 backend OVERVIEW_RECEIVABLE_DETAIL_RANK_LIMIT 一致 */
export const OVERVIEW_RECEIVABLE_DETAIL_RANK_LIMIT = 80;

/** 与首页超期柱同序：发货尾款 + 验收尾款各 10 档 */
const RECEIVABLE_AGING_BUCKET_KEYS = {
	delivery: [
		'totalDeliveryOverdue_30_orders',
		'totalDeliveryOverdue_10_30_orders',
		'totalDeliveryOverdue_0_10_orders',
		'totalDelivery_0_10_orders',
		'totalDelivery_10_30_orders',
		'totalDelivery_30_60_orders',
		'totalDelivery_60_90_orders',
		'totalDelivery_90_180_orders',
		'totalDelivery_180_360_orders',
		'totalDelivery_360_plus_orders',
	],
	accept: [
		'totalAcceptOverdue_30_orders',
		'totalAcceptOverdue_10_30_orders',
		'totalAcceptOverdue_0_10_orders',
		'totalAccept_0_10_orders',
		'totalAccept_10_30_orders',
		'totalAccept_30_60_orders',
		'totalAccept_60_90_orders',
		'totalAccept_90_180_orders',
		'totalAccept_180_360_orders',
		'totalAccept_360_plus_orders',
	],
};

const collectFilteredOrderIds = (orders = [], orderIds = null) => {
	if (Array.isArray(orderIds) && orderIds.length > 0) {
		return new Set(orderIds.map((id) => id?.toString?.() || String(id)).filter(Boolean));
	}
	const ids = new Set();
	for (const order of orders ?? []) {
		const mongoId = order?._id?.toString?.();
		const bizId = order?.orderId?.toString?.();
		if (mongoId) ids.add(mongoId);
		if (bizId) ids.add(bizId);
	}
	return ids;
};

const resolveOverdueEntryKey = (item) => {
	const orderId = item?.order_id?.toString?.() || item?.orderId?.toString?.() || '';
	return item?.ar_id?.toString?.() || `${orderId}_${item?.scheduleNode_id}_${item?.title}_${item?.overdueDay}`;
};

/** 12 月序列按自然季求和 → 4 点（金额/单数可加总） */

/** 应收排行：客户 / 销售 */
export const buildOverviewReceivableRankRows = (orders = [], dimension = COMPOSITION_CHART_DIMENSION.CUSTOMER, { limit = 5 } = {}) => {
	const { items, totalMetric } = buildCompositionChartRows({
		orders,
		dimension,
		metric: COMPOSITION_CHART_METRIC.RECEIVABLE,
	});
	const rows = (items ?? [])
		.filter((row) => (Number(row.receivableTotal) || 0) > 0)
		.slice(0, limit)
		.map((row) => ({
			key: row.key,
			name: row.label || '未分类',
			amount: Number(row.receivableTotal) || 0,
			count: Number(row.count) || 0,
		}));
	return { rows, totalReceivable: Number(totalMetric) || 0 };
};

/** 超期应收：从超期 API 条目中筛当前订单集（与顶栏筛选订单对齐） */

/** 超期应收：从超期 API 条目中筛当前订单集（与顶栏筛选订单对齐） */
export const sumFilteredOverdueReceivable = (orders = [], overdueData = {}, orderIdsList = null) => {
	const orderIds = collectFilteredOrderIds(orders, orderIdsList);
	if (!orderIds.size || !overdueData) return 0;

	const bucketKeys = [
		'totalDeliveryOverdue_30_orders',
		'totalDeliveryOverdue_10_30_orders',
		'totalDeliveryOverdue_0_10_orders',
		'totalAcceptOverdue_30_orders',
		'totalAcceptOverdue_10_30_orders',
		'totalAcceptOverdue_0_10_orders',
	];
	const seen = new Set();
	let total = 0;
	for (const key of bucketKeys) {
		const list = overdueData?.[key]?.orderids;
		if (!Array.isArray(list)) continue;
		for (const item of list) {
			const orderId = item?.order_id?.toString?.() || item?.orderId?.toString?.();
			if (!orderId || !orderIds.has(orderId)) continue;
			if (!(Number(item?.overdueDay) < 0)) continue;
			const arId = resolveOverdueEntryKey(item);
			if (seen.has(arId)) continue;
			seen.add(arId);
			total += Number(item?.accountsReceivable) || 0;
		}
	}
	return total;
};

/**
 * 应收统计卡：优先服务端 overviewReceivable（主扫同锅）；否则回退 orderIds ∩ overdue。
 */

/**
 * 应收统计卡：优先服务端 overviewReceivable（主扫同锅）；否则回退 orderIds ∩ overdue。
 */
export const buildReceivableOverviewBundleFromStats = (stats, overdueData = {}, { limit = 5 } = {}) => {
	const server = stats?.overviewReceivable;
	if (server && Array.isArray(server.receivable)) {
		return {
			labels: Array.isArray(server.labels) && server.labels.length ? server.labels : [...CHART_LABELS],
			receivable: server.receivable,
			overdue: server.overdue ?? [],
			receivableCounts: server.receivableCounts ?? [],
			overdueCounts: server.overdueCounts ?? [],
			totalReceivable: Number(server.totalReceivable) || 0,
			overdueTotal: Number(server.overdueTotal) || 0,
			customerRows: (server.customerRows ?? []).slice(0, limit),
			saleRows: (server.saleRows ?? []).slice(0, limit),
			overdueCustomerRows: (server.overdueCustomerRows ?? []).slice(0, limit),
			overdueSaleRows: (server.overdueSaleRows ?? []).slice(0, limit),
		};
	}
	return buildReceivableOverviewBundle(stats?.orders, overdueData, {
		limit,
		orderIdsList: stats?.orderIds,
	});
};

/**
 * 应收统计卡统一数据源（折线 / 双环 / 列表 / KPI 共用）：
 * 从 overdueData 账龄桶筛当前统计订单集，一次遍历产出账龄序列与销售/客户排行。
 * orderIdsList：服务端轻量 id 列表（无全量 orders 时用）。
 */
export const buildReceivableOverviewBundle = (orders = [], overdueData = {}, { limit = 5, orderIdsList = null } = {}) => {
	const labels = [...CHART_LABELS];
	const len = labels.length;
	const receivable = Array.from({ length: len }, () => 0);
	const overdue = Array.from({ length: len }, () => 0);
	const receivableCounts = Array.from({ length: len }, () => 0);
	const overdueCounts = Array.from({ length: len }, () => 0);
	const empty = {
		labels,
		receivable,
		overdue,
		receivableCounts,
		overdueCounts,
		totalReceivable: 0,
		overdueTotal: 0,
		customerRows: [],
		saleRows: [],
		overdueCustomerRows: [],
		overdueSaleRows: [],
	};

	const orderIds = collectFilteredOrderIds(orders, orderIdsList);
	if (!orderIds.size || !overdueData) return empty;

	const seenEntries = new Set();
	const receivableOrderSets = Array.from({ length: len }, () => new Set());
	const overdueOrderSets = Array.from({ length: len }, () => new Set());
	const customerMap = new Map();
	const saleMap = new Map();
	const overdueCustomerMap = new Map();
	const overdueSaleMap = new Map();

	const bumpRank = (map, key, name, amount, orderId) => {
		if (!key) return;
		const prev = map.get(key) ?? { key, name: name || '未分类', amount: 0, orderIds: new Set() };
		if (name) prev.name = name;
		prev.amount += amount;
		if (orderId) prev.orderIds.add(orderId);
		map.set(key, prev);
	};

	const bucketPairs = RECEIVABLE_AGING_BUCKET_KEYS.delivery.map((deliveryKey, index) => [deliveryKey, RECEIVABLE_AGING_BUCKET_KEYS.accept[index], index]);

	for (const [deliveryKey, acceptKey, bucketIndex] of bucketPairs) {
		for (const key of [deliveryKey, acceptKey]) {
			const list = overdueData?.[key]?.orderids;
			if (!Array.isArray(list)) continue;
			for (const item of list) {
				const orderId = item?.order_id?.toString?.() || item?.orderId?.toString?.();
				if (!orderId || !orderIds.has(orderId)) continue;
				const entryKey = resolveOverdueEntryKey(item);
				if (seenEntries.has(entryKey)) continue;
				seenEntries.add(entryKey);
				const amount = Number(item?.accountsReceivable) || 0;
				if (!(amount > 0)) continue;

				receivable[bucketIndex] += amount;
				receivableOrderSets[bucketIndex].add(orderId);

				const customerKey = item?.customerId?.toString?.() || item?.customerName?.trim?.() || '__unknown_customer__';
				const customerName = item?.customerName?.trim?.() || '未知客户';
				const saleName = item?.saleUserName?.trim?.() || '未知销售';
				const saleKey = saleName;

				if (bucketIndex < CHART_BUCKETS_RECENT_START_INDEX) {
					overdue[bucketIndex] += amount;
					overdueOrderSets[bucketIndex].add(orderId);
					bumpRank(overdueCustomerMap, customerKey, customerName, amount, orderId);
					bumpRank(overdueSaleMap, saleKey, saleName, amount, orderId);
				}

				bumpRank(customerMap, customerKey, customerName, amount, orderId);
				bumpRank(saleMap, saleKey, saleName, amount, orderId);
			}
		}
	}

	for (let i = 0; i < len; i += 1) {
		receivableCounts[i] = receivableOrderSets[i].size;
		overdueCounts[i] = overdueOrderSets[i].size;
	}

	const toRankRows = (map) =>
		[...map.values()]
			.map((row) => ({
				key: row.key,
				name: row.name,
				amount: row.amount,
				count: row.orderIds.size,
			}))
			.filter((row) => row.amount > 0)
			.sort((a, b) => b.amount - a.amount || a.name.localeCompare(b.name, 'zh-CN'))
			.slice(0, limit);

	const totalReceivable = receivable.reduce((acc, n) => acc + n, 0);
	const overdueTotal = overdue.reduce((acc, n) => acc + n, 0);

	return {
		labels,
		receivable,
		overdue,
		receivableCounts,
		overdueCounts,
		totalReceivable,
		overdueTotal,
		customerRows: toRankRows(customerMap),
		saleRows: toRankRows(saleMap),
		overdueCustomerRows: toRankRows(overdueCustomerMap),
		overdueSaleRows: toRankRows(overdueSaleMap),
	};
};

/** 第三行物流卡：待发货 → 已发货 → 物流完成 → 已关单（互斥；已关单优先） */
